import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';
import './ContainerDataTapeValidator.css';

interface ValidationError {
  type: 'structure' | 'data';
  row?: number;
  column?: string;
  columnLetter?: string;
  message: string;
}

interface ValidationResult {
  success: boolean;
  isValid: boolean;
  errors: ValidationError[];
  warnings: ValidationError[];
  summary: {
    totalErrors: number;
    totalWarnings: number;
    structureErrors: number;
    dataErrors: number;
  };
}

function ContainerDataTapeValidator() {
  const [file, setFile] = useState<File | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      setValidationResult(null);
      setError(null);
    }
  };

  const handleValidate = async () => {
    if (!file) {
      setError('Please select a file to validate.');
      return;
    }

    setIsValidating(true);
    setError(null);
    setValidationResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_BASE_URL}/data-tape/validate`, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.message || 'Validation failed');
      }

      setValidationResult(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to validate data tape');
    } finally {
      setIsValidating(false);
    }
  };

  return (
    <div className="ContainerDataTapeValidator">
      <header className="ContainerDataTapeValidator-header">
        <Link to="/" className="back-link">
          <img 
            src="https://app.soteriamarket.com/banner3.jpeg" 
            alt="Soteria Logo" 
            className="logo"
          />
        </Link>
        <h1>Data Tape Validator</h1>
      </header>
      
      <main className="ContainerDataTapeValidator-main">
        <div className="validator-container">
          <div className="upload-section">
            <h2>Upload Data Tape File</h2>
            <p className="upload-description">
              Upload a Soteria Data Tape Excel file to validate its structure and data integrity.
            </p>
            
            <div className="file-input-wrapper">
              <input
                type="file"
                id="file-upload"
                accept=".xlsx,.xls,.xlsm"
                onChange={handleFileChange}
                disabled={isValidating}
              />
              <label htmlFor="file-upload" className="file-input-label">
                {file ? file.name : 'Choose Excel file...'}
              </label>
            </div>

            <button
              className="validate-button"
              onClick={handleValidate}
              disabled={!file || isValidating}
            >
              {isValidating ? 'Validating...' : 'Validate Data Tape'}
            </button>

            {error && (
              <div className="error-message">
                {error}
              </div>
            )}
          </div>

          {validationResult && (
            <div className="validation-results">
              <div className={`validation-summary ${validationResult.isValid ? 'valid' : 'invalid'}`}>
                <h2>
                  {validationResult.isValid ? '✓ Validation Passed' : '✗ Validation Failed'}
                </h2>
                <div className="summary-stats">
                  <div className="stat">
                    <span className="stat-label">Total Errors:</span>
                    <span className="stat-value">{validationResult.summary.totalErrors}</span>
                  </div>
                  <div className="stat">
                    <span className="stat-label">Structure Errors:</span>
                    <span className="stat-value">{validationResult.summary.structureErrors}</span>
                  </div>
                  <div className="stat">
                    <span className="stat-label">Data Errors:</span>
                    <span className="stat-value">{validationResult.summary.dataErrors}</span>
                  </div>
                  <div className="stat">
                    <span className="stat-label">Warnings:</span>
                    <span className="stat-value">{validationResult.summary.totalWarnings}</span>
                  </div>
                </div>
              </div>

              {validationResult.errors.length > 0 && (
                <div className="errors-section">
                  <h3>Errors</h3>
                  <div className="errors-list">
                    {validationResult.errors.map((err, idx) => (
                      <div key={idx} className="error-item">
                        <div className="error-header">
                          <span className="error-type">{err.type}</span>
                          {err.row && err.columnLetter && err.column && (
                            <span className="error-location">Row {err.row}, Column {err.columnLetter} ({err.column})</span>
                          )}
                          {err.row && !err.columnLetter && (
                            <span className="error-location">Row {err.row}</span>
                          )}
                          {!err.row && err.column && (
                            <span className="error-location">Column {err.column}</span>
                          )}
                        </div>
                        <span className="error-message">{err.message}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {validationResult.warnings.length > 0 && (
                <div className="warnings-section">
                  <h3>Warnings</h3>
                  <div className="warnings-list">
                    {validationResult.warnings.map((warn, idx) => (
                      <div key={idx} className="warning-item">
                        <div className="warning-header">
                          <span className="warning-type">{warn.type}</span>
                          {warn.row && warn.columnLetter && warn.column && (
                            <span className="warning-location">Row {warn.row}, Column {warn.columnLetter} ({warn.column})</span>
                          )}
                          {warn.row && !warn.columnLetter && (
                            <span className="warning-location">Row {warn.row}</span>
                          )}
                          {!warn.row && warn.column && (
                            <span className="warning-location">Column {warn.column}</span>
                          )}
                        </div>
                        <span className="warning-message">{warn.message}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export default ContainerDataTapeValidator;
